import { useEffect, useRef } from "react";
import { featureIcons } from "../../assets/assets";
import "./LottieIcon.scss";

let lottieLoaderPromise = null;
const loadLottie = () => {
  if (lottieLoaderPromise) return lottieLoaderPromise;
  lottieLoaderPromise = import("lottie-web/build/player/lottie_light").then(mod => mod.default || mod);
  return lottieLoaderPromise;
};

// Simple SVG block used when no lottie animation is found for a varKey
const FallbackIcon = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.4 }}>
    <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <circle cx="12" cy="12" r="3" fill="currentColor" />
  </svg>
);

/**
 * LottieIcon – renders a Lottie animation as a compact icon.
 * Accepts either:
 *   icon  – a lottie JSON object (animationData), OR
 *   varKey – a string key looked up in featureIcons map.
 *
 * Resting state: paused on last frame (visible, static).
 * Desktop: plays once from frame 0 on mouseenter, returns to last frame.
 * Mobile:  plays once from frame 0 on touchstart, returns to last frame.
 *
 * Optional:
 *   triggerRef – a React ref pointing to an outer element. When provided,
 *                mouseenter on that element also triggers the animation,
 *                in addition to hovering the icon itself.
 */
const LottieIcon = ({ icon, varKey, size = 32, style: extraStyle, triggerRef }) => {
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const completeHandlerRef = useRef(null);

  // Resolve the animation data — prefer explicit icon prop, then varKey lookup
  const animationData = (icon && typeof icon === "object")
    ? icon
    : (varKey ? featureIcons[varKey] : null);

  useEffect(() => {
    if (!animationData) return;

    let isMounted = true;

    loadLottie().then((lottie) => {
      if (!isMounted || !containerRef.current) return;
      const anim = lottie.loadAnimation({
        container: containerRef.current,
        renderer: "svg",
        loop: false,
        autoplay: false,
        animationData,
        rendererSettings: { preserveAspectRatio: "xMidYMid meet" },
      });
      // Once loaded, jump to last frame so the icon is visible at rest
      anim.addEventListener("DOMLoaded", () => {
        anim.goToAndStop(anim.totalFrames - 1, true);
      });
      animationRef.current = anim;
    });

    return () => {
      isMounted = false;
      if (animationRef.current) {
        animationRef.current.destroy();
        animationRef.current = null;
      }
    };
  }, [animationData]);

  const playAnimation = () => {
    const anim = animationRef.current;
    if (!anim) return;
    // Remove any previous complete listener
    if (completeHandlerRef.current) {
      anim.removeEventListener("complete", completeHandlerRef.current);
    }
    const onComplete = () => {
      anim.goToAndStop(anim.totalFrames - 1, true);
    };
    completeHandlerRef.current = onComplete;
    anim.addEventListener("complete", onComplete);
    anim.goToAndPlay(0, true);
  };

  // Attach mouseenter listener to the external triggerRef element when provided
  useEffect(() => {
    const el = triggerRef?.current;
    if (!el) return;
    el.addEventListener("mouseenter", playAnimation);
    return () => el.removeEventListener("mouseenter", playAnimation);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerRef, animationData]);

  // String icon (emoji / text) — render as-is
  if (icon && typeof icon === "string") {
    return (
      <span style={{ fontSize: size * 0.75, lineHeight: 1, display: "inline-flex", alignItems: "center" }}>
        {icon}
      </span>
    );
  }

  // No animation data available — show a neutral placeholder
  if (!animationData) {
    return <FallbackIcon size={size} />;
  }

  return (
    <div
      className="feature-icon"
      style={{ width: size, height: size, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", ...extraStyle }}
      onMouseEnter={playAnimation}
      onTouchStart={(e) => { e.stopPropagation(); playAnimation(); }}
    >
      <div ref={containerRef} style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }} />
    </div>
  );
};

export default LottieIcon;
